import { z } from "zod";

/**
 * Sözleşme TARAFI (CLOS-111 `[073]`).
 *
 * ### Tek model, çok tür
 *
 * Kart: *"çalışma alanı tarafı, karşı taraf, kuruluş, gerçek kişi ve müvekkil AYNI GENEL
 * TARAF MODELİNİN desteklenen türleridir; biri diğerinin yerine ZORLA DÖNÜŞTÜRÜLMEZ."*
 * Beş ayrı tablo yazsaydık her biri kendi rol, kimlik ve audit kurallarını taşır, biri gün
 * gelip diğerinden ayrışırdı.
 */
export const CONTRACT_PARTY_TYPES = [
  /** Sözleşmenin bizim tarafımız — aktif çalışma alanı. */
  "workspace",
  "counterparty",
  "client",
  "organization",
  "person",
] as const;
export type ContractPartyType = (typeof CONTRACT_PARTY_TYPES)[number];

export const CONTRACT_PARTY_TYPE_NAMES: Record<ContractPartyType, string> = {
  workspace: "Çalışma alanı",
  counterparty: "Karşı taraf",
  client: "Müvekkil",
  organization: "Kuruluş",
  person: "Gerçek kişi",
};

/**
 * TARAF KONUMU — sözleşmedeki taraf ekseni (CLOS-1108 · rapor 4 §12.2).
 *
 * ### ⚠ Neden AYRI bir eksen: `partyType` iki soruyu tek alanda eziyordu
 *
 * `CONTRACT_PARTY_TYPES` iki farklı ekseni birleştiriyor:
 *   workspace · counterparty  →  KONUM ("hangi taraftalar?")
 *   organization · person     →  VARLIK TÜRÜ ("kim?")
 *
 * Tek dropdown olduğu için iki şey imkânsızdı:
 *   1. *"Bizimle aynı tarafta"* — rapor §12.2'nin saydığı üçüncü konum hiç yoktu
 *   2. *GERÇEK KİŞİ olan KARŞI TARAF* — ikisi birbirini dışlıyordu, oysa rapor §5.1 ve
 *      §5.2 şirket tarafı ile gerçek kişi tarafını AYRI AYRI sayıyor ve ikisinden de
 *      davet gönderilebilmesini istiyor
 *
 * Rapor bunu önceden uyarmıştı: *"mevcut role semantiğine gizlice yüklenmemelidir."*
 *
 * ### ⚠ `partyType` KALDIRILMADI — expand-contract (kriter 16)
 *
 * Eski alan yerinde duruyor ve eski satırlar okunmaya devam ediyor. Konum bilinmiyorsa
 * `null` — UYDURULMUYOR. `organization`/`person`/`client` türündeki eski bir satırın hangi
 * tarafta olduğunu veri söylemiyor; "karşı taraf" diye doldurmak, ölçülmemiş bir gerçeği
 * kayda yazmak olurdu.
 */
/**
 * ⚠ `represented` KANONİK, `workspace` UYUMLULUK (Principal Remediation §12.2 · K4/B).
 *
 * Rapor `WORKSPACE` legal side'ı yeni yazımlarda YASAKLIYOR: "aktif çalışma alanı"
 * sözleşmeyi kimin YÖNETTİĞİNİ söyler, hukuki tarafın kim olduğunu söylemez. Açık
 * principal seçimini eski değerle saklamak, birkaç ay sonra kodda yeniden
 * `workspace = temsil edilen taraf` anlamını doğururdu.
 *
 * `workspace` KALDIRILMADI ve eski satırlar okunmaya devam ediyor; yasak olan yeni yazım.
 */
export const CONTRACT_PARTY_POSITIONS = [
  "represented",
  "workspace",
  "same_side",
  "counterparty",
] as const;
export type ContractPartyPosition = (typeof CONTRACT_PARTY_POSITIONS)[number];

export const CONTRACT_PARTY_POSITION_NAMES: Record<ContractPartyPosition, string> = {
  represented: "Temsil edilen taraf",
  /** ⚠ Eski kayıtların etiketi; yeni seçimde sunulmuyor (CLOS-1267). */
  workspace: "Bizim tarafımız (eski kayıt)",
  /**
   * ⚠ ÖRNEK ETİKETİN İÇİNDE (CLOS-1267 dönüşü). "Aynı taraftaki diğer taraf" bilmece gibi
   * okunuyordu; Hasan sordu: *"bu ne demek kanka?"*. Konumun ne olduğunu açıklamayı
   * okumadan anlatan şey SOMUT ÖRNEK: kefil ve ortak, kullanıcının zaten bildiği roller.
   */
  same_side: "Bizim yanımızdaki taraf (kefil, ortak)",
  counterparty: "Karşı taraf",
};

/**
 * SEÇİLEBİLİR KONUMLAR — YAZMA listesi (CLOS-1267).
 *
 * ### ⚠ İKİ LİSTE, İKİ SORU
 *
 * `CONTRACT_PARTY_POSITIONS` OKUMA listesi: eski satırları çizebilmek için `workspace`
 * taşımak zorunda (expand-contract, kriter 16). Bu liste ise kanonun YENİ YAZIMDA izin
 * verdiği küme. Tek liste bırakıldığı sürece arayüz, kodun yazılmasını yasakladığı bir
 * değeri kullanıcıya seçtiriyordu — canlıda ölçüldü.
 *
 * ### ⚠ Eleme ARAYÜZDE DEĞİL BURADA
 *
 * Seçenekleri yalnız ekranda süzseydik kural ekranda yaşardı: uç `workspace` kabul etmeye
 * devam eder ve bir sonraki istemci (mobil, entegrasyon, betik) aynı kusuru yeniden
 * üretirdi. Girdi şeması bu listeden besleniyor.
 */
export const SECILEBILIR_PARTY_POSITIONS = CONTRACT_PARTY_POSITIONS.filter(
  (konum): konum is Exclude<ContractPartyPosition, "workspace"> => konum !== "workspace",
);

/**
 * Seçeneğin NE DEMEK olduğu — etiketin tek başına ayırt etmediği ölçüldü.
 *
 * Hasan canlı panelde sordu: *"bunlar ne demek oluyor, hepsi aynı şeyi söylemiyor mu?"*
 * Haklıydı: dört etiketin üçü, alanı bilmeyen birine aynı cümleyi söylüyor. Açıklama
 * ekranda duruyor; kullanıcı "hangisi benim tarafım" sorusunu tahminle cevaplamıyor.
 */
export const CONTRACT_PARTY_POSITION_HINTS: Record<ContractPartyPosition, string> = {
  represented:
    "Bu sözleşmede temsil ettiğimiz asıl taraf — kendi şirketimiz ya da adına çalıştığımız müvekkil.",
  workspace: "Eski kayıtlardan gelen konum; yeni taraflarda 'Temsil edilen taraf' kullanılıyor.",
  same_side:
    "Bizim safımızda duran ama temsil ettiğimiz asıl taraf olmayan biri: kefil, müşterek borçlu, konsorsiyum ortağı.",
  counterparty: "Masanın öbür tarafı — sözleşmeyi bizimle müzakere eden taraf.",
};

/**
 * Eski `partyType`'tan konum TÜRETME — yalnız TARTIŞMASIZ olanlar.
 *
 * ⚠ `client`, `organization` ve `person` için `null` dönüyor ve bu bilinçli: o değerler
 * varlık türünü söylüyor, konumu değil. Tahmin etseydik göç, ölçülmemiş bir veriyi
 * ölçülmüş gibi kaydederdi.
 */
export function positionFromLegacyType(type: ContractPartyType): ContractPartyPosition | null {
  if (type === "workspace") return "workspace";
  if (type === "counterparty") return "counterparty";
  return null;
}

/**
 * Tarafın etkin konumu: yeni alan varsa o, yoksa eski türden türetilen.
 *
 * ⚠ Mantık BU FONKSİYONDAN geçiyor (`missingPartyRequirements`, karşı taraf revizyonu).
 * İki kaynağı çağrı yerlerinde tek tek birleştirseydik biri güncellenir, diğeri unutulurdu.
 */
export function effectivePartyPosition(party: {
  position?: ContractPartyPosition | null;
  partyType: ContractPartyType;
}): ContractPartyPosition | null {
  return party.position ?? positionFromLegacyType(party.partyType);
}

/**
 * TEMSİL EDİLEN TARAF mı? (Principal Remediation §4.3)
 *
 * ### ⚠ `workspace` artık "çalışma alanının kendisi" demek DEĞİL
 *
 * Kanonik karar: *"Sözleşmenin hukuki tarafı, aktif çalışma alanından değil gerçek hukuki
 * ilişkiden çözülür."* Konum ekseni korunuyor (`same_side` gerçek bir hukuki durum: ortak
 * davacı, alt yüklenici) ama `workspace` değerinin ANLAMI değişiyor: artık "bu sözleşmede
 * BİZİM SAFIMIZDA duran ASIL taraf" — kim olduğunu `resolveContractPrincipal` söylüyor,
 * aktif tenant değil.
 *
 * ### ⚠ Enum DEĞİŞTİRİLMEDİ ve bu bilinçli
 *
 * `REPRESENTED_SIDE` diye yeni bir değer yazsaydık üç maliyet doğardı: 56 satırlık veri
 * göçü, `party_type` türetmesinin ikinci kez kırılması ve `same_side`'ın karşılıksız
 * kalması. Rapor da isim değil SEMANTİK istiyor: *"Repository mevcut naming'i koruyacaksa
 * isim farklı olabilir; semantik aynı olmalıdır."*
 *
 * Temsil edilen saf = `workspace` ∪ `same_side`. Karşı saf = `counterparty`.
 * Konumu bilinmeyen (NULL) satır hiçbir safta sayılmaz — tahmin edilmez.
 */
export function isRepresentedSide(party: {
  position?: ContractPartyPosition | null;
  partyType: ContractPartyType;
}): boolean {
  const konum = effectivePartyPosition(party);
  return konum === "represented" || konum === "workspace" || konum === "same_side";
}

/**
 * KANONİK asıl taraf (principal) mı?
 *
 * ### ⚠ KONUM YETMEZ, AÇIK İRADE GEREKİR
 *
 * `position === "workspace"` bakıp "principal" demek, yeni modelin düzelttiği kusuru geri
 * getirirdi: hukuk bürosu bağlamındaki eski satır çoğu zaman BÜRONUN KENDİSİ ve o, taraf
 * DEĞİL. Ölçüldü (6 Eylül 2026, dev): 25 legacy `workspace` satırının 18'i hangi hukuki
 * tarafı gösterdiği belirsiz.
 *
 * Bu yüzden ölçüt `principalSource`: yalnız açık principal komutu dolduruyor. Alanı
 * ZORUNLU parametre yaptık ki çağıran "konuma bakıp karar vereyim" diyemesin.
 */
export function isPrincipalParty(party: {
  position?: ContractPartyPosition | null;
  partyType: ContractPartyType;
  principalSource?: ContractPrincipalSource | null;
}): boolean {
  if (!party.principalSource) return false;
  return effectivePartyPosition(party) !== "counterparty";
}

/**
 * TARAFIN TÜRÜ (CLOS-1124 / CLOS-1150) — KONUMDAN AYRI İKİNCİ EKSEN.
 *
 * `party_type` üç soruyu tek kolonda karıştırıyordu (biz miyiz / müvekkil mi / kişi mi).
 * Rapor 3 §4: taraf = KONUM (bizim taraf · aynı saf · karşı saf) × TÜR (gerçek kişi ·
 * kuruluş). `party_type` okuyucular kırılmasın diye KORUNUR ve sunucuda TÜRETİLİR;
 * istemci artık konum + tür gönderir.
 *
 * ⚠ Eski `counterparty` satırlarının türü NULL kalır (Halit kararı 3, 5 Eyl 2026): kimse
 * bilmiyorsa tahmin edilmez; düzenlemede seçim ZORUNLU (`contractPartyInputSchema`).
 */
/**
 * PRINCIPAL KAYNAĞI — temsil edilen tarafın hangi ilişkiden doğduğu (K4/B).
 *
 * ⚠ UI'da "taraf türü" olarak GÖSTERİLMEZ; taraf türü `entityType`. Bu alan provenance:
 * kararın dayanağı neydi.
 */
export const CONTRACT_PRINCIPAL_SOURCES = [
  "self",
  "company",
  "client",
  "client_workspace",
] as const;
export type ContractPrincipalSource = (typeof CONTRACT_PRINCIPAL_SOURCES)[number];

export const CONTRACT_PRINCIPAL_SOURCE_NAMES: Record<ContractPrincipalSource, string> = {
  self: "Kendi adınıza",
  company: "Kurum",
  client: "Müvekkil",
  client_workspace: "Müvekkil",
};

export const CONTRACT_PARTY_ENTITY_TYPES = ["person", "organization"] as const;
export type ContractPartyEntityType = (typeof CONTRACT_PARTY_ENTITY_TYPES)[number];

export const CONTRACT_PARTY_ENTITY_TYPE_NAMES: Record<ContractPartyEntityType, string> = {
  person: "Gerçek kişi",
  organization: "Kuruluş",
};

/** Tür bilinmiyorsa kartta yazan metin — boş bırakmak "kuruluş sanırım" demekten dürüst. */
export const CONTRACT_PARTY_ENTITY_TYPE_UNKNOWN = "Tür belirtilmedi";

/**
 * Yeni yazımda `party_type` TÜRETİLİR: bizim konumumuz → `workspace`; kanonik müvekkil
 * bağı → `client`; aksi hâlde türün kendisi. `counterparty` artık YAZILMAZ — o soruyu
 * konum kolonu cevaplıyor.
 */
export function deriveContractPartyType(input: {
  position: ContractPartyPosition;
  entityType: ContractPartyEntityType;
  canonicalRef?: string | null;
}): ContractPartyType {
  /**
   * ⚠ `represented` eski `party_type`'a "workspace" diye yazılmıyor: yeni kanonik konum,
   * eski karışık ekseni geri çağırmasın. Tür yine gerçek varlık türü.
   */
  if (input.position === "workspace") return "workspace";
  /**
   * ⚠ `client` ARTIK TÜRETİLMİYOR (K7 · Principal Remediation §1). Kanonik karar:
   * *"`client` bir hukuki VARLIK TÜRÜ değildir; müvekkil/temsil ilişkisidir."* Kanonik
   * bağı olan bir tarafın türü yine gerçek varlık türüdür; müvekkil olduğu bilgisi
   * `canonical_ref` bağında durur, tür kolonunda değil. Eski satırlardaki `client`
   * değeri okunmaya devam ediyor (expand-contract).
   */
  return input.entityType;
}

/**
 * İstemci eski `partyType`'ı da gönderdiyse konum/türle ÇELİŞMEMELİ; çelişki sessizce
 * ezilmez, 422 olur — iki kaynak aynı soruya iki cevap veremez. `client`/`organization`/
 * `person` türü bizim konumumuzla ÇELİŞMEZ (0188'den beri müvekkil bizim safımızda
 * olabiliyor); sunucu o durumda `workspace` türetir, kanonik bağ `canonical_ref`'te kalır.
 */
export function contractPartyTypeConflict(input: {
  partyType?: ContractPartyType | null;
  position: ContractPartyPosition;
  entityType: ContractPartyEntityType;
}): string | null {
  const eski = input.partyType;
  if (!eski) return null;
  /**
   * ⚠ ESKİ `workspace` TÜRÜ KANONİK KONUMA TAŞINABİLİR (CLOS-1267).
   *
   * Eskiden her sapma reddediliyordu; `workspace` konumu yeni yazımda yasaklanınca bu
   * kural eski satırları KİLİTLERDİ: kullanıcı kaydı düzenlemek istediğinde ne eski
   * değeri seçebilir ne de yenisine geçebilirdi. `represented` o değerin kanonik ardılı,
   * dolayısıyla çelişki değil GÖÇ.
   */
  if (eski === "workspace" && input.position !== "workspace" && input.position !== "represented") {
    return "Çalışma alanı türü yalnız bizim tarafımız konumuyla kullanılabilir";
  }
  if (eski === "counterparty" && input.position !== "counterparty") {
    return "Karşı taraf türü yalnız karşı taraf konumuyla kullanılabilir";
  }
  if (eski === "person" && input.entityType !== "person") {
    return "Gerçek kişi türü kuruluş seçimiyle çelişiyor";
  }
  if (eski === "organization" && input.entityType !== "organization") {
    return "Kuruluş türü gerçek kişi seçimiyle çelişiyor";
  }
  return null;
}

/**
 * Kart etiketi (rapor 3 §6): "Karşı taraf · Kuruluş". Tür NULL ise "Tür belirtilmedi",
 * konum NULL ise "Konum belirtilmedi" — teknik kimlik ya da uydurma değer yok.
 */
export function partyDescriptor(party: {
  position?: ContractPartyPosition | null;
  partyType: ContractPartyType;
  entityType?: ContractPartyEntityType | null;
}): string {
  const konum = effectivePartyPosition(party);
  const tur = party.entityType ?? null;
  return [
    konum ? CONTRACT_PARTY_POSITION_NAMES[konum] : "Konum belirtilmedi",
    tur ? CONTRACT_PARTY_ENTITY_TYPE_NAMES[tur] : CONTRACT_PARTY_ENTITY_TYPE_UNKNOWN,
  ].join(" · ");
}

/**
 * Taraf ROLLERİ — sözleşmedeki konum.
 *
 * Tür ile rol AYRI eksenler: bir kuruluş hem "satıcı" hem "alıcı" olabilir ve aynı gerçek
 * kişi iki sözleşmede iki farklı rolde bulunabilir. Tek alana sıkıştırılsaydı "kuruluş
 * olarak alıcı" gibi birleşik değerler biriktirirdik.
 */
export const CONTRACT_PARTY_ROLES = ["buyer", "seller", "employer", "employee", "other"] as const;
export type ContractPartyRole = (typeof CONTRACT_PARTY_ROLES)[number];

export const CONTRACT_PARTY_ROLE_NAMES: Record<ContractPartyRole, string> = {
  buyer: "Alıcı",
  seller: "Satıcı",
  employer: "İşveren",
  employee: "Çalışan",
  other: "Diğer",
};

/** Türkçe normalizasyon — `[052]` ve `[051]` ile aynı tuzak ve aynı çözüm. */
function normalize(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLocaleLowerCase("tr");
}

/**
 * Tarafın KİMLİK ANAHTARI (ADR-0024).
 *
 * ⚠ Eldeki EN GÜÇLÜ tanımlayıcı kazanıyor ve sıra sabit: kanonik referans → resmî kimlik →
 * e-posta → ad. Sıra sabit olmasaydı aynı taraf iki farklı anahtar üretir ve tekillik
 * kısıtı hiçbir şeyi engellemezdi.
 *
 * Önek (`ref:`, `reg:`, …) şart: öneksiz `"12345"` bir vergi numarası mı yoksa bir isim mi
 * olduğunu söylemez ve iki farklı taraf aynı anahtara düşebilirdi.
 */
export function partyIdentityKey(input: {
  canonicalRef?: string | null;
  registryNumber?: string | null;
  email?: string | null;
  displayName: string;
}): string {
  if (input.canonicalRef) return `ref:${input.canonicalRef}`;
  if (input.registryNumber) return `reg:${normalize(input.registryNumber).replace(/\s/g, "")}`;
  if (input.email) return `mail:${normalize(input.email)}`;
  return `name:${normalize(input.displayName)}`;
}

/**
 * GİRDİ SÖZLEŞMESİ (CLOS-1124): konum + tür ZORUNLU; `partyType` yalnız eski istemciler
 * için kabul edilir, sunucu asıl değeri `deriveContractPartyType` ile türetir. Türü NULL
 * olan eski bir satır bu şemadan geçmeden GÜNCELLENEMEZ — Halit kararı 3'ün kapısı.
 */
export const contractPartyInputSchema = z
  .object({
    /**
     * ⚠ YAZMA listesi (CLOS-1267): `workspace` kabul EDİLMİYOR. Kanon onu yeni yazımda
     * yasaklıyor; şema kabul ettiği sürece yasak yalnız bir yorum satırıydı.
     */
    position: z.enum(SECILEBILIR_PARTY_POSITIONS, "Geçerli bir taraf konumu seçilmelidir"),
    entityType: z.enum(
      CONTRACT_PARTY_ENTITY_TYPES,
      "Taraf türü seçilmelidir: gerçek kişi ya da kuruluş",
    ),
    partyType: z.enum(CONTRACT_PARTY_TYPES, "Geçerli bir taraf türü seçilmelidir").nullish(),
    role: z.enum(CONTRACT_PARTY_ROLES, "Geçerli bir taraf rolü seçilmelidir"),
    displayName: z
      .string({ error: "Taraf adı zorunludur" })
      .trim()
      .min(1, "Taraf adı zorunludur")
      .max(200, "Taraf adı en fazla 200 karakter olabilir"),
    canonicalRef: z.uuid().nullish(),
    registryNumber: z.string().max(50, "Kimlik numarası en fazla 50 karakter olabilir").nullish(),
    email: z.email("Geçerli bir e-posta adresi giriniz").nullish(),
    phone: z.string().max(30, "Telefon en fazla 30 karakter olabilir").nullish(),
    contactName: z.string().max(200).nullish(),
  })
  .superRefine((girdi, ctx) => {
    const celiski = contractPartyTypeConflict(girdi);
    if (celiski) ctx.addIssue({ code: "custom", path: ["partyType"], message: celiski });
  });

export type ContractPartyInput = z.infer<typeof contractPartyInputSchema>;

/**
 * TARAFIN ORTAK ÇALIŞMA DURUMU (K1 · CLOS-1094).
 *
 * ⚠ BU ALAN ERİŞİM ANLATMIYOR (R4 §14.2). Sözleşmenin hukuki tarafı olmak Closer
 * erişimi vermez ve davet kabulü kimseyi hukuki taraf yapmaz. Durum yalnız şunu
 * söylüyor: bu tarafla Closer üzerinde ortak çalışma kurulmaya çalışıldı mı, kuruldu mu.
 *
 * ⚠ TARAF SATIRINDA TÜRETİLİYOR, KOLONDA TUTULMUYOR: kaynak iki tabloda
 * (`contract_org_invitations` bekleyen satırı, `contract_org_participants` aktif satırı).
 * Kolona yazsaydık üçüncü bir gerçek üretir ve iki tanesi zamanla ayrışırdı.
 */
export const CONTRACT_PARTY_COLLABORATION_STATES = ["none", "pending", "active", "ended"] as const;
export type ContractPartyCollaborationState = (typeof CONTRACT_PARTY_COLLABORATION_STATES)[number];

export const CONTRACT_PARTY_COLLABORATION_NAMES: Record<ContractPartyCollaborationState, string> = {
  none: "Davet edilmedi",
  pending: "Davet bekliyor",
  active: "Davet kabul edildi",
  ended: "Erişim sonlandırıldı",
};

export const contractPartyAuthorizedUserSchema = z.object({
  name: z.string(),
  status: z.enum(["active"]),
});
export type ContractPartyAuthorizedUser = z.infer<typeof contractPartyAuthorizedUserSchema>;

export const contractPartyCollaborationSchema = z.object({
  state: z.enum(CONTRACT_PARTY_COLLABORATION_STATES),
  /** Bekleyen davetin kimliği — iptal ucu buradan çağrılıyor. */
  invitationId: z.string().nullable(),
  /** Davetin gittiği adres — yalnız `pending` durumunda dolu. */
  targetEmail: z.string().nullable(),
  linkedTenantId: z.string().nullable(),
  linkedTenantName: z.string().nullable(),
  expiresAt: z.string().nullable(),
  /** Aktif katılımın kimliği (geri çekme hedefi); pending/none'da null. */
  participantId: z.string().nullable(),
  /** Davetin/katılımın kişi mi kurum mu olduğu (snapshot, CLOS-1125 S1); bilinmiyorsa null. */
  principalKind: z.enum(["person", "organization"]).nullable(),
  /** Karşı tarafa açılan erişim tavanı (davette seçilen preset/set). */
  permissions: z.array(z.string()),
  /**
   * Karşı organizasyonun bu sözleşmeye ERİŞİM VERDİĞİ kullanıcılar (CLOS-1125 / Halit S4):
   * yalnız explicit kayıt erişimi olanlar; karşı kurumun üye dizini ASLA. Davet eden görür,
   * yönetemez (rol/kaldırma/yeni kişi yok).
   */
  authorizedUsers: z.array(contractPartyAuthorizedUserSchema),
});
export type ContractPartyCollaboration = z.infer<typeof contractPartyCollaborationSchema>;

/** Hiç davet edilmemiş tarafın durumu — tek yerde tanımlı, çağıranlar kopyalamıyor. */
export const CONTRACT_PARTY_COLLABORATION_NONE: ContractPartyCollaboration = {
  state: "none",
  invitationId: null,
  targetEmail: null,
  linkedTenantId: null,
  linkedTenantName: null,
  expiresAt: null,
  participantId: null,
  principalKind: null,
  permissions: [],
  authorizedUsers: [],
};

/**
 * GÖRÜNÜM: `partyType` sunucuda hep dolu (türetilmiş ya da eski), `position`/`entityType`
 * eski satırlarda NULL olabilir — istemci "belirtilmedi" çizer, tahmin etmez.
 */
export const contractPartySchema = z.object({
  id: z.uuid(),
  partyType: z.enum(CONTRACT_PARTY_TYPES),
  position: z.enum(CONTRACT_PARTY_POSITIONS).nullish(),
  entityType: z.enum(CONTRACT_PARTY_ENTITY_TYPES).nullable(),
  role: z.enum(CONTRACT_PARTY_ROLES),
  displayName: z.string(),
  canonicalRef: z.uuid().nullish(),
  registryNumber: z.string().nullish(),
  email: z.string().nullish(),
  phone: z.string().nullish(),
  contactName: z.string().nullish(),
  identityKey: z.string(),
  createdAt: z.string(),
  collaboration: contractPartyCollaborationSchema,
});

export type ContractParty = z.infer<typeof contractPartySchema>;

/**
 * EN AZ GEREKLİ taraf yapısı (kart kriteri).
 *
 * Bir sözleşme en az İKİ taraf ister ve biri çalışma alanının kendisi olmalı: karşılıklı
 * olmayan bir metin sözleşme değildir ve bizim tarafımızın kim olduğu bilinmeyen bir kayıt,
 * imza akışında kimin adına imza atılacağını söyleyemez.
 *
 * ⚠ Kural EKLEME anında değil TAMLIK sorulduğunda uygulanıyor: tarafları tek tek eklemek
 * gerekiyor ve ilk tarafı eklerken "iki taraf lazım" demek, akışı imkânsız kılardı.
 */
export function missingPartyRequirements(
  parties: readonly { partyType: ContractPartyType; position?: ContractPartyPosition | null }[],
): string[] {
  const eksikler: string[] = [];
  if (parties.length < 2) eksikler.push("Sözleşmede en az iki taraf bulunmalıdır");
  /**
   * ⚠ KONUMDAN soruluyor, türden değil (CLOS-1108). Soru "bizim tarafımız kim?" ve bu bir
   * konum sorusu; eski satırlarda `partyType === "workspace"` aynı cevabı veriyor ve
   * `effectivePartyPosition` ikisini birleştiriyor.
   */
  /**
   * ⚠ ARTIK "ÇALIŞMA ALANI TARAFI" ARANMIYOR (Principal Remediation §3 BUG-02, §25.2).
   *
   * Eski kural `Sözleşmede çalışma alanı tarafı bulunmalıdır` diyordu ve bu, aktif
   * çalışma alanını hukuki taraf olmaya İTİYORDU: hukuk bürosu kendi adını sözleşmeye
   * taraf diye yazmadıkça sözleşme "eksik" görünüyordu. Oysa büro sözleşmeyi YÖNETİR,
   * tarafı OLMAZ; taraf müvekkildir.
   *
   * Yeni kural hukuki tamlığı soruyor: bir tarafta temsil edilen, diğerinde karşı taraf.
   * İki eksiklik AYRI AYRI raporlanıyor — "iki taraf lazım" tek başına hangi tarafın
   * eksik olduğunu söylemiyordu.
   */
  if (!parties.some((p) => isRepresentedSide(p))) {
    eksikler.push("Sözleşmede temsil edilen taraf bulunmalıdır");
  }
  if (!parties.some((p) => effectivePartyPosition(p) === "counterparty")) {
    eksikler.push("Sözleşmede karşı taraf bulunmalıdır");
  }
  return eksikler;
}

/**
 * TEMSİL EDİLEN TARAF ATAMA GİRDİSİ (K4/B).
 *
 * ⚠ İki mod AYRI dallar: "kendi adıma" hiçbir kimlik istemiyor, "müvekkil adına" kanonik
 * müvekkil kimliği istiyor. Tek nesnede opsiyonel alanla ifade etseydik "mod client ama
 * clientId yok" gibi geçersiz bir istek şemadan geçerdi.
 */
export const setContractPrincipalSchema = z.discriminatedUnion("mode", [
  z.strictObject({ mode: z.literal("self") }),
  z.strictObject({
    mode: z.literal("client"),
    clientId: z.uuid("Geçerli bir müvekkil seçilmelidir"),
  }),
]);
export type SetContractPrincipalInput = z.infer<typeof setContractPrincipalSchema>;

/**
 * TARAF ADINA UYGUN AKTÖR SORGUSU (Mega E2E BUG-011).
 *
 * ⚠ Yetenek WHITELIST: istemci serbest metin gönderemiyor. Liste ile kapı aynı anahtar
 * kümesini konuşmazsa, seçici bir gün kapının tanımadığı bir yetenek için aday üretir.
 */
export const eligibleActorsQuerySchema = z.object({
  capability: z.enum(["contract.reconcile", "contract.sign"], "Geçerli bir yetenek seçilmelidir"),
});

export const contractPartyEligibleActorsParamsSchema = z.object({
  id: z.uuid(),
  partyId: z.uuid(),
});
